import { useCustomerBranding } from '@/hooks/use-customer-branding';
import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHouse,
  faBuilding,
  faScrewdriverWrench,
  faClipboardList,
  faCircleCheck,
  faXmark,
  faRightFromBracket,
} from '@fortawesome/free-solid-svg-icons';
import { useCustomerUiStore } from '@/stores/customer-ui.store';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { customerAuthService } from '@/services/customer-auth.service';
import { CUSTOMER_PORTAL_ROUTES } from '@/routes/customer-portal-routes';

/**
 * Customer Module Phase 5 — Dashboard + Equipment + Jobs implemented.
 * Phase 6 (Authorization & Communication) is reachable from within Job
 * Detail, not a separate nav item — Authorization Cases are always
 * Job-scoped, never an independent browse-all-cases surface. No placeholder
 * links for unimplemented future modules (Documents — Phase 7+).
 *
 * Uses the isolated `useCustomerUiStore` (not the shared `useUiStore`) so a
 * staff/platform sidebar state in the same browser profile can never
 * collapse this one. Desktop: fixed, always expanded. Below `lg`: an
 * overlay drawer (UI_UX_STANDARDS Sidebar) opened from the header; the
 * everyday mobile navigation is the bottom tab bar (CustomerMobileNav).
 */
export function CustomerPortalSidebar() {
  const { brandName, logoUrl } = useCustomerBranding();
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => setLogoFailed(false), [logoUrl]);
  const isMobileSidebarOpen = useCustomerUiStore((s) => s.isMobileSidebarOpen);
  const setMobileSidebarOpen = useCustomerUiStore((s) => s.setMobileSidebarOpen);
  const user = useCustomerAuthStore((s) => s.user);
  const refreshToken = useCustomerAuthStore((s) => s.refreshToken);
  const clearAuth = useCustomerAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isMobileSidebarOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileSidebarOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isMobileSidebarOpen, setMobileSidebarOpen]);

  const handleLogout = async () => {
    try {
      if (refreshToken) await customerAuthService.logout(refreshToken);
    } catch {
      // ignore — still clear local state
    }
    clearAuth();
    setMobileSidebarOpen(false);
    navigate(CUSTOMER_PORTAL_ROUTES.LOGIN);
  };

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    [
      'customer-pressable flex min-h-11 w-full items-center gap-3 rounded-customer-sm px-3 py-2 text-sm transition-colors duration-150',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-offset-2 focus-visible:ring-offset-customer-surface',
      isActive
        ? 'bg-customer-primary/10 font-semibold text-customer-primary-text'
        : 'text-customer-text-secondary hover:bg-customer-text/[0.05] hover:text-customer-text',
    ].join(' ');

  return (
    <>
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/45 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        role={isMobileSidebarOpen ? 'dialog' : undefined}
        aria-modal={isMobileSidebarOpen ? true : undefined}
        aria-label={isMobileSidebarOpen ? 'Navigation' : undefined}
        className={[
          'fixed inset-y-0 left-0 z-50 flex h-screen w-[min(18rem,85vw)] flex-col border-r border-customer-border bg-customer-surface shadow-customer-lg transition-transform duration-300 lg:relative lg:z-auto lg:w-64 lg:shadow-none',
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        ].join(' ')}
      >
        <div className="flex h-customer-header shrink-0 items-center gap-2.5 border-b border-customer-border px-4 lg:h-16">
          {logoUrl && !logoFailed ? (
            <img
              src={logoUrl}
              alt=""
              className="h-8 w-auto max-w-[6rem] shrink-0 object-contain object-left"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-customer-sm bg-customer-primary">
              <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-customer-action-ink" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <span
              className="block truncate text-sm font-semibold tracking-tight text-customer-text"
              title={brandName}
            >
              {brandName}
            </span>
            <span className="block text-[10px] font-semibold uppercase tracking-widest text-customer-primary-text">
              Customer portal
            </span>
          </div>
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(false)}
            aria-label="Close navigation"
            className="customer-pressable flex h-11 w-11 shrink-0 items-center justify-center rounded-customer-sm text-customer-text-secondary hover:bg-customer-text/[0.06] hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring lg:hidden"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </div>

        {user && (
          <div className="border-b border-customer-border px-4 py-3 lg:hidden">
            <p className="truncate text-sm font-medium text-customer-text">
              {`${user.firstName} ${user.lastName}`.trim()}
            </p>
            <p className="truncate text-xs text-customer-text-secondary">{user.customerName}</p>
          </div>
        )}

        <nav
          className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3"
          aria-label="Customer portal navigation"
        >
          <NavLink
            to={CUSTOMER_PORTAL_ROUTES.DASHBOARD}
            className={linkCls}
            onClick={() => setMobileSidebarOpen(false)}
          >
            <FontAwesomeIcon icon={faHouse} className="h-4 w-4 shrink-0" />
            <span>Dashboard</span>
          </NavLink>
          <NavLink
            to={CUSTOMER_PORTAL_ROUTES.EQUIPMENT}
            className={linkCls}
            onClick={() => setMobileSidebarOpen(false)}
          >
            <FontAwesomeIcon icon={faScrewdriverWrench} className="h-4 w-4 shrink-0" />
            <span>Equipment</span>
          </NavLink>
          <NavLink
            to={CUSTOMER_PORTAL_ROUTES.JOBS}
            className={linkCls}
            onClick={() => setMobileSidebarOpen(false)}
          >
            <FontAwesomeIcon icon={faClipboardList} className="h-4 w-4 shrink-0" />
            <span>Jobs</span>
          </NavLink>
          {/* D-130 Phase 6R — the decision queue belongs in the chrome. It is
              where a customer acts, not merely reads, and before 6R the only
              way in was a dashboard link or a typed URL. */}
          <NavLink
            to={CUSTOMER_PORTAL_ROUTES.DECISIONS}
            className={linkCls}
            onClick={() => setMobileSidebarOpen(false)}
          >
            <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 shrink-0" />
            <span>Decisions</span>
          </NavLink>
        </nav>

        <div className="customer-safe-bottom border-t border-customer-border p-2 lg:hidden">
          <button
            type="button"
            onClick={() => void handleLogout()}
            className="customer-pressable flex min-h-11 w-full items-center gap-3 rounded-customer-sm px-3 text-sm font-medium text-customer-danger-text hover:bg-customer-danger/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
          >
            <FontAwesomeIcon icon={faRightFromBracket} className="h-4 w-4 shrink-0" />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
